// Third-person orbit camera with voxel collision, smoothing, shake and cinematic paths.
import * as THREE from 'three';
import { clamp, damp } from './util.js';
import { BLOCKS } from './blocks.js';

export const ZOOM = { min: 3.2, max: 18, default: 7.5, eye: 0, eyeThreshold: 1.6 };

export class CameraRig {
  constructor(camera) {
    this.camera = camera;
    this.yaw = 0;
    this.pitch = 0.36;
    this.dist = 7.5;
    this.distCur = 7.5;
    this.focus = new THREE.Vector3();
    this.shakeAmt = 0;
    this.shakeT = 0;
    this.cine = null;
    this.idle = 0;
    this.autoYaw = null;
    this.firstPerson = false;
    this.allowFirstPerson = false;
  }

  setDistance(distance) {
    this.dist = clamp(Number.isFinite(distance) ? distance : ZOOM.default, this.allowFirstPerson ? ZOOM.eye : ZOOM.min, ZOOM.max);
    return this.dist;
  }

  zoom(delta) { return this.setDistance(this.dist + delta); }

  snap(target, yaw = this.yaw) {
    this.yaw = yaw;
    this.focus.set(target.x, target.y + 1.5, target.z);
    this.distCur = this.dist;
    this.apply(null, 0);
  }

  shake(a) {
    this.shakeAmt = Math.min(1.2, this.shakeAmt + a);
  }

  update(dt, input, target, world, opts = {}) {
    if (this.cine) return this.updateCine(dt);
    const sens = input.touchMode ? 0.0062 : 0.0052;
    if (input.camDX || input.camDY) this.idle = 0;
    else this.idle += dt;
    this.yaw -= input.camDX * sens;
    this.pitch = clamp(this.pitch + input.camDY * sens * 0.8, -1.35, 1.35);
    this.zoom(input.wheel * 0.006);
    if (input.wasPressed?.('=') || input.wasPressed?.('+')) this.zoom(-1);
    if (input.wasPressed?.('-') || input.wasPressed?.('_')) this.zoom(1);
    if (input.wasPressed?.('0')) this.setDistance(ZOOM.default);
    this.eyeSuppressed = !!opts.forceThirdPerson;
    this.firstPerson = this.allowFirstPerson && !this.eyeSuppressed && this.dist <= ZOOM.eyeThreshold;
    // gentle auto-follow: when running and not steering the camera, ease behind the course direction
    if (opts.followYaw !== undefined && this.idle > 2.5 && opts.moving) {
      let d = opts.followYaw - this.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      this.yaw += d * Math.min(1, dt * 0.6);
    }
    const tx = target.x, ty = target.y + (this.firstPerson ? 1.62 : 1.45), tz = target.z;
    this.focus.x = damp(this.focus.x, tx, 16, dt);
    this.focus.z = damp(this.focus.z, tz, 16, dt);
    const dy = ty - this.focus.y;
    this.focus.y = damp(this.focus.y, ty, Math.abs(dy) > 3 ? 14 : 7, dt);
    if(this.firstPerson) {
      this.focus.set(tx,ty,tz);
    }
    this.apply(world, dt);
  }

  apply(world, dt) {
    if (this.firstPerson) {
      this.camera.position.copy(this.focus);
      this.camera.lookAt(this.focus.x + Math.sin(this.yaw) * Math.cos(this.pitch),
        this.focus.y - Math.sin(this.pitch), this.focus.z + Math.cos(this.yaw) * Math.cos(this.pitch));
      this.camera.updateMatrixWorld();
      return;
    }
    const cp = Math.cos(this.pitch), sp = Math.sin(this.pitch);
    const dx = -Math.sin(this.yaw) * cp, dy = sp, dz = -Math.cos(this.yaw) * cp;
    let want = Math.max(this.eyeSuppressed ? ZOOM.default : ZOOM.min, this.dist);
    if (world) {
      const hit = world.raycast(this.focus.x, this.focus.y, this.focus.z, dx, dy, dz, want + 0.4, (id) => BLOCKS[id].solid && BLOCKS[id].opaque);
      if (hit) want = Math.max(1.0, hit.dist - 0.35);
    }
    if (!dt || want < this.distCur) this.distCur = want;
    else this.distCur = damp(this.distCur, want, 3.5, dt);
    const c = this.camera;
    c.position.set(this.focus.x + dx * this.distCur, this.focus.y + dy * this.distCur, this.focus.z + dz * this.distCur);
    if (this.shakeAmt > 0.001) {
      this.shakeT += dt || 0;
      const s = this.shakeAmt * this.shakeAmt * 0.35;
      c.position.x += (Math.sin(this.shakeT * 71) + Math.sin(this.shakeT * 33)) * s;
      c.position.y += (Math.sin(this.shakeT * 63) + Math.sin(this.shakeT * 29)) * s;
      c.position.z += Math.sin(this.shakeT * 57) * s;
      this.shakeAmt = Math.max(0, this.shakeAmt - (dt || 0) * 2.2);
    }
    c.lookAt(this.focus);
    c.updateMatrixWorld();
  }

  // cinematic flyover along points; resolves when finished
  playPath(points, duration, lookAhead = 0.08) {
    return new Promise((resolve) => {
      const curve = new THREE.CatmullRomCurve3(points.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
      this.cine = { curve, t: 0, duration, lookAhead, resolve };
    });
  }

  skipCine() {
    if (this.cine) {
      const r = this.cine.resolve;
      this.cine = null;
      r();
    }
  }

  updateCine(dt) {
    const C = this.cine;
    C.t += dt / C.duration;
    const k = Math.min(1, C.t);
    const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
    const p = C.curve.getPoint(e);
    const la = C.curve.getPoint(Math.min(1, e + C.lookAhead));
    this.camera.position.copy(p);
    this.camera.lookAt(la.x, la.y - 2, la.z);
    if (k >= 1) {
      this.cine = null;
      C.resolve();
    }
  }
}
